import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, FileText, Folder, MessageSquare, CheckCircle, Info } from 'lucide-react';
import notificationService from '../services/notificationService';
import { useAuth } from '../App.jsx';
import { useNavigate } from 'react-router-dom';

const NotificationsDropdown = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [activeTab, setActiveTab] = useState('all');
    const dropdownRef = useRef(null);
    const echoRef = useRef(null);

    // Cerrar al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchNotifications = async () => {
        try {
            const data = await notificationService.getNotifications(1);
            setNotifications(data.data);
            setUnreadCount(data.unread_count);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    // Inicializar WebSockets y obtener iniciales
    useEffect(() => {
        if (!user) return;

        fetchNotifications();

        let token = null;
        try {
            const stored = localStorage.getItem('cbr_auth');
            if (stored) {
                token = JSON.parse(stored).token;
            }
        } catch (e) {
            console.error('Error parsing cbr_auth', e);
        }

        if (token) {
            const echo = notificationService.initEcho(token);
            echoRef.current = echo;

            // Escuchar notificaciones del usuario (Laravel envía al canal App.Models.User.{id})
            if (echo) {
                echo.private(`App.Models.User.${user.id}`)
                    .notification((notification) => {
                        // Agregar nueva notificación al inicio de la lista
                        setNotifications(prev => [notification, ...prev]);
                        setUnreadCount(prev => prev + 1);
                    });
            }
        }

        return () => {
            if (echoRef.current && user) {
                echoRef.current.leave(`App.Models.User.${user.id}`);
            }
        };
    }, [user]);

    const handleMarkAsRead = async (id, e) => {
        e?.stopPropagation();
        try {
            await notificationService.markAsRead(id);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleDeleteRead = async () => {
        try {
            await notificationService.deleteRead();
            setNotifications(prev => prev.filter(n => !n.read_at));
        } catch (error) {
            console.error('Error deleting read notifications:', error);
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.read_at) {
            handleMarkAsRead(notification.id);
        }
        setIsOpen(false);
        const actionUrl = notification.action_url || notification.data?.action_url;
        if (actionUrl) {
            navigate(actionUrl);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'tramite': return <FileText size={18} className="text-blue-400" />;
            case 'solicitud': return <CheckCircle size={18} className="text-green-400" />;
            case 'archivero': return <Folder size={18} className="text-yellow-400" />;
            case 'chat': return <MessageSquare size={18} className="text-purple-400" />;
            default: return <Info size={18} className="text-gray-400" />;
        }
    };

    const filteredNotifications = notifications.filter(n => {
        if (activeTab === 'all') return true;
        const type = n.type === 'App\\Notifications\\SystemNotification' ? n.data?.type : (n.type || n.data?.type);
        return type === activeTab;
    });

    const timeAgo = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);
        if (seconds < 60) return 'Hace un momento';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `Hace ${minutes} m`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `Hace ${hours} h`;
        const days = Math.floor(hours / 24);
        return `Hace ${days} d`;
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    position: 'relative',
                    background: 'var(--bg-body)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '8px',
                    cursor: 'pointer',
                    color: 'var(--text-main)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'border-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span style={{
                        position: 'absolute', top: -5, right: -5,
                        background: 'red', color: 'white',
                        fontSize: '10px', fontWeight: 'bold',
                        padding: '2px 6px', borderRadius: '10px',
                        border: '2px solid var(--bg-surface)'
                    }}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 12px)', right: 0,
                    width: '400px',
                    background: '#1a222d', // AWS dark mode style
                    border: '1px solid #2d3748',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
                    zIndex: 1000,
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                    fontFamily: 'Inter, sans-serif'
                }}>
                    <div style={{ padding: '16px', borderBottom: '1px solid #2d3748', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, color: '#f7fafc', fontSize: '1rem', fontWeight: 600 }}>Notificaciones</h3>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            {unreadCount > 0 && (
                                <button onClick={handleMarkAllAsRead} style={{ background: 'none', border: 'none', color: '#63b3ed', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}>
                                    Marcar todas como leídas
                                </button>
                            )}
                            {notifications.some(n => n.read_at) && (
                                <button onClick={handleDeleteRead} style={{ background: 'none', border: 'none', color: '#fc8181', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}>
                                    Limpiar leídas
                                </button>
                            )}
                            <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: '#a0aec0', cursor: 'pointer', padding: 0 }}>
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', borderBottom: '1px solid #2d3748', padding: '0 16px', gap: '20px' }}>
                        {['all', 'tramite', 'solicitud', 'chat'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    background: 'none', border: 'none',
                                    padding: '12px 0', cursor: 'pointer',
                                    color: activeTab === tab ? '#63b3ed' : '#a0aec0',
                                    fontWeight: activeTab === tab ? 600 : 500,
                                    borderBottom: activeTab === tab ? '2px solid #63b3ed' : '2px solid transparent',
                                    fontSize: '0.85rem', textTransform: 'capitalize'
                                }}
                            >
                                {tab === 'all' ? `Más recientes (${unreadCount})` : tab}
                            </button>
                        ))}
                    </div>

                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {filteredNotifications.length === 0 ? (
                            <div style={{ padding: '32px', textAlign: 'center', color: '#a0aec0', fontSize: '0.9rem' }}>
                                No hay notificaciones en esta categoría.
                            </div>
                        ) : (
                            filteredNotifications.map(notification => (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    style={{
                                        padding: '16px',
                                        borderBottom: '1px solid #2d3748',
                                        display: 'flex', gap: '12px',
                                        cursor: 'pointer',
                                        background: notification.read_at ? 'transparent' : 'rgba(99, 179, 237, 0.05)',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                                    onMouseOut={(e) => e.currentTarget.style.background = notification.read_at ? 'transparent' : 'rgba(99, 179, 237, 0.05)'}
                                >
                                    <div style={{ flexShrink: 0, marginTop: '2px' }}>
                                        {getIcon(notification.type === 'App\\Notifications\\SystemNotification' ? notification.data?.type : (notification.type || notification.data?.type))}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.85rem' }}>{notification.title || notification.data?.title}</span>
                                            <span style={{ color: '#a0aec0', fontSize: '0.75rem' }}>{timeAgo(notification.created_at)}</span>
                                        </div>
                                        <div style={{ color: '#cbd5e0', fontSize: '0.8rem', lineHeight: 1.4 }}>
                                            {notification.message || notification.data?.message}
                                        </div>
                                    </div>
                                    {!notification.read_at && (
                                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                            <div style={{ width: 8, height: 8, background: '#63b3ed', borderRadius: '50%' }} />
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsDropdown;
